// @ts-nocheck
import _ from 'lodash';
import PropTypes from 'prop-types';
import omitEmpty from 'omit-empty';
import {
  makeStyles,
  Box,
  FormControlLabel,
  Grid,
  Icon,
  TextField,
  Button,
  Typography,
  Checkbox,
  MenuItem,
} from '@material-ui/core';
import { KeyboardDatePicker } from '@material-ui/pickers';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { useIfMounted } from '../../../../common/hooks';
import { DateUtils } from '../../../../common/utils';

const useStyles = makeStyles(theme => ({
  container: {
    flex: '1',
    display: 'flex',
  },
  form: {
    flex: '1',
    display: 'flex',
    flexDirection: 'column',
    '& .MuiFormControl-root': {
      minHeight: '78px',
    },
    '& .MuiFormGroup-root': {
      minHeight: '192px',
      paddingTop: '4px',
    },
  },
  formContent: {
    flex: '1',
    display: 'flex',
    flexWrap: 'wrap',
    padding: theme.spacing(1, 3),
    overflow: 'auto',
  },
  checkbox: {
    marginTop: '6px',
    pointerEvents: 'none',
  },
  switch: {
    margin: '-4px 0 0 0',
    alignItems: 'flex-start',
    '& .MuiSwitch-root': {
      marginLeft: '-8px',
    },
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: theme.spacing(1),
  },
  button: {
    margin: theme.spacing(1),
    '&:last-child': {
      marginRight: '0',
    },
  },
}));

function CampaignSeriesForm(props) {
  const [t] = useTranslation();
  const { control, errors, formState, reset, register, trigger, getValues, handleSubmit } =
    useForm();
  const classes = useStyles();
  const ifMounted = useIfMounted();

  const {
    campaignId,
    series,
    seriesSegments,
    onSave,
    onCancel,
    actionsDisabled,
    editMode,
    cancelButtonText,
    cancelButtonIcon,
    saveButtonText,
    saveButtonIcon,
  } = props;

  const { isDirty, isSubmitted, isSubmitting } = formState;
  const onSubmit = async data => {
    const formData = omitEmpty(data);

    if (series?.id) {
      formData.id = series.id;
    }
    formData.campaignId = campaignId;
    formData.segment = {};
    formData.prefix = _.defaultTo(series?.prefix, data.prefix);
    formData.segmentCode = _.defaultTo(series?.segment.code, data.segmentCode);
    formData.startDate = _.defaultTo(series?.startDate, data.startDate);
    formData.voucherCount = _.defaultTo(series?.voucherCount, _.toNumber(data.voucherCount));
    try {
      await onSave(formData);
      ifMounted(() => reset(data));
    } catch (e) {
      // Error already handled
    }
  };

  const validateStartDate = value => {
    const endDate = getValues('endDate');
    if (!DateUtils.isValid(value)) {
      return t('campaign.campaignSeriesStartDateInvalid');
    }
    if (DateUtils.compareDates(value, endDate) > 0) {
      return t('campaign.campaignSeriesStartDateAfterEndDate');
    }
    return true;
  };

  const handleStartDateChange = (params, value) => {
    params.onChange(_.defaultTo(value?.format('YYYY-MM-DDTHH:mm:ssZ'), ''));
    if (isSubmitted) {
      trigger('endDate');
    }
  };

  const validateEndDate = value => {
    const startDate = getValues('startDate');
    if (!DateUtils.isValid(value)) {
      return t('campaign.campaignSeriesEndDateInvalid');
    }
    if (DateUtils.compareDates(value, startDate) < 0) {
      return t('campaign.campaignSeriesEndDateBeforeStartDate');
    }
    return true;
  };

  const validateSegment = value => {
    if (value === 'UNKNOWN') {
      return t('campaign.campaignSeriesSegmentTitleRequired');
    }
    return true;
  };

  const handleEndDateChange = (params, value) => {
    params.onChange(_.defaultTo(value?.format('YYYY-MM-DDTHH:mm:ssZ'), ''));
    if (isSubmitted) {
      trigger('startDate');
    }
  };

  return (
    <Box id="CampaignSeriesForm-container" className={classes.container}>
      <form id="CampaignSeriesForm-form" className={classes.form} onSubmit={handleSubmit(onSubmit)}>
        <Box className={classes.formContent}>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <TextField
                id="CampaignSeriesForm-prefix"
                name="prefix"
                label={t('campaign.campaignSeriesPrefix')}
                variant="outlined"
                defaultValue={series?.prefix}
                InputLabelProps={{ shrink: editMode }}
                inputRef={register({
                  maxLength: {
                    value: 8,
                    message: t('campaign.campaignSeriesPrefixMaxLength'),
                  },
                })}
                error={!!errors.prefix}
                helperText={errors.prefix?.message}
                disabled={editMode}
                fullWidth
              />
            </Grid>
            <Grid item xs={8}>
              <TextField
                id="CampaignSeriesForm-title"
                name="title"
                label={t('campaign.campaignSeriesTitle')}
                variant="outlined"
                defaultValue={series?.title}
                InputLabelProps={{ shrink: editMode }}
                inputRef={register({
                  required: t('campaign.campaignSeriesTitleRequired'),
                })}
                error={!!errors.title}
                helperText={errors.title?.message}
                fullWidth
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                id="CampaignSeriesForm-voucherCount"
                name="voucherCount"
                label={t('campaign.campaignSeriesVoucherCount')}
                variant="outlined"
                defaultValue={series?.voucherCount}
                InputLabelProps={{ shrink: editMode }}
                inputRef={register({
                  required: t('campaign.campaignSeriesVoucherCountRequired'),
                  pattern: {
                    value: /^\d+$/,
                    message: t('campaign.campaignSeriesVoucherCountInteger'),
                  },
                })}
                error={!!errors.voucherCount}
                helperText={errors.voucherCount?.message}
                disabled={editMode}
                fullWidth
              />
            </Grid>
            <Grid item xs={8}>
              <Controller
                name="segmentCode"
                control={control}
                defaultValue={_.defaultTo(series?.segment.code, 'UNKNOWN')}
                rules={{
                  required: t('campaign.campaignSeriesSegmentTitleRequired'),
                  validate: validateSegment,
                }}
                as={
                  <TextField
                    id="CampaignSeriesForm-segmentCode"
                    label={t('campaign.campaignSeriesSegmentCode')}
                    variant="outlined"
                    disabled={editMode}
                    fullWidth
                    select
                    error={!!errors.segmentCode}
                    helperText={errors.segmentCode?.message}
                  >
                    {seriesSegments.map(segment => (
                      <MenuItem key={segment.code} value={segment.code}>
                        {segment.title}
                      </MenuItem>
                    ))}
                  </TextField>
                }
              />
            </Grid>
            <Grid item xs={4}>
              <Controller
                name="startDate"
                control={control}
                defaultValue={_.defaultTo(
                  series
                    ? DateUtils.format(
                        series.startDate?.replace(/(\[.+\])/g, ''),
                        'YYYY-MM-DDTHH:mm:ssZ'
                      )
                    : null,
                  DateUtils.format(DateUtils.now('YYYY-MM-DD'), 'YYYY-MM-DDTHH:mm:ssZ')
                )}
                rules={{
                  required: t('campaign.campaignSeriesStartDateRequired'),
                  validate: validateStartDate,
                }}
                render={params => (
                  <KeyboardDatePicker
                    id="CampaignSeriesForm-startDate"
                    label={t('campaign.campaignSeriesStartDate')}
                    variant="inline"
                    inputVariant="outlined"
                    format="DD-MM-YYYY"
                    value={params.value}
                    onChange={value => handleStartDateChange(params, value)}
                    error={!!errors.startDate}
                    helperText={errors.startDate?.message}
                    disabled={editMode}
                    autoOk
                    fullWidth
                  />
                )}
              />
            </Grid>
            <Grid item xs={4}>
              <Controller
                name="endDate"
                control={control}
                defaultValue={_.defaultTo(
                  series
                    ? DateUtils.format(
                        series.endDate?.replace(/(\[.+\])/g, ''),
                        'YYYY-MM-DDTHH:mm:ssZ'
                      )
                    : null,
                  DateUtils.format(DateUtils.now('YYYY-MM-DD'), 'YYYY-MM-DDTHH:mm:ssZ')
                )}
                rules={{
                  required: t('campaign.campaignSeriesEndDateRequired'),
                  validate: validateEndDate,
                }}
                render={params => (
                  <KeyboardDatePicker
                    id="CampaignSeriesForm-endDate"
                    label={t('campaign.campaignSeriesEndDate')}
                    variant="inline"
                    inputVariant="outlined"
                    format="DD-MM-YYYY"
                    value={params.value}
                    onChange={value => handleEndDateChange(params, value)}
                    error={!!errors.endDate}
                    helperText={errors.endDate?.message}
                    autoOk
                    fullWidth
                  />
                )}
              />
            </Grid>
            <Grid item xs={4}>
              <FormControlLabel
                label={t('campaign.campaignSeriesExported')}
                labelPlacement="end"
                className={classes.checkbox}
                control={
                  <Controller
                    name="exported"
                    control={control}
                    defaultValue={_.defaultTo(series?.exported, false)}
                    render={params => (
                      <Checkbox
                        id="CampaignSeriesForm-exported"
                        checked={params.value}
                        onChange={e => params.onChange(e.target.checked)}
                        disabled
                      />
                    )}
                  />
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                id="CampaignSeriesForm-description"
                name="description"
                label={t('campaign.campaignSeriesDescription')}
                variant="outlined"
                defaultValue={series?.description}
                InputLabelProps={{ shrink: editMode }}
                inputRef={register}
                rows={4}
                multiline
                fullWidth
              />
            </Grid>
          </Grid>
        </Box>
        <Box className={classes.actions}>
          <Button
            id="CampaignSeriesForm-cancel"
            className={classes.button}
            startIcon={<Icon>{_.defaultTo(cancelButtonIcon, 'cancel')}</Icon>}
            onClick={onCancel}
            disabled={actionsDisabled || isSubmitting}
          >
            <Typography variant="button" noWrap>
              {_.defaultTo(cancelButtonText, t('common.cancel'))}
            </Typography>
          </Button>
          <Button
            id="CampaignSeriesForm-save"
            type="submit"
            color="primary"
            className={classes.button}
            startIcon={<Icon>{_.defaultTo(saveButtonIcon, 'save')}</Icon>}
            disabled={actionsDisabled || isSubmitting || !isDirty}
          >
            <Typography variant="button" noWrap>
              {_.defaultTo(saveButtonText, t('common.save'))}
            </Typography>
          </Button>
        </Box>
      </form>
    </Box>
  );
}

CampaignSeriesForm.defaultProps = {
  series: undefined,
  cancelButtonText: undefined,
  cancelButtonIcon: undefined,
  saveButtonText: undefined,
  saveButtonIcon: undefined,
  actionsDisabled: undefined,
  editMode: undefined,
};

CampaignSeriesForm.propTypes = {
  campaignId: PropTypes.number.isRequired,
  series: PropTypes.shape({
    id: PropTypes.number,
    campaignId: PropTypes.number,
    title: PropTypes.string,
    startDate: PropTypes.string,
    prefix: PropTypes.string,
    endDate: PropTypes.string,
    exported: PropTypes.bool,
    voucherCount: PropTypes.number,
    voucherRedeemed: PropTypes.number,
    description: PropTypes.string,
    segment: PropTypes.shape({
      code: PropTypes.string,
      title: PropTypes.string,
    }),
  }),
  cancelButtonText: PropTypes.string,
  cancelButtonIcon: PropTypes.string,
  saveButtonText: PropTypes.string,
  saveButtonIcon: PropTypes.string,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  actionsDisabled: PropTypes.bool,
  editMode: PropTypes.bool,
  seriesSegments: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default CampaignSeriesForm;
